import React, { Fragment, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

import HeaderV2 from "../../components/headerV2/HeaderV2";
import FooterV2 from "../../components/footerV2/FooterV2";
import AboutHero from "../../components/aboutHero/AboutHero";
import useScrollReveal from "../../hooks/useScrollReveal";

import metadataJSON from "../../data/metadata.json";
import schemadataJSON from "../../data/schemadata.json";
import "../AboutUsPage/aboutV2.css";

/* Photo supplied in the Sep 2026 website review (lives in /public). */
const AchievementsImg = process.env.PUBLIC_URL + "/images/about-v2/achievements.jpg";

const STATS = [
  { n: "8.8K", plus: true, l: "Total registrations" },
  { n: "9", plus: false, l: "States reached" },
  { n: "106", plus: true, l: "Donation camps" },
  { n: "6K", plus: true, l: "Blood units collected" },
  { n: "1,000", plus: true, l: "QR sticker locations" },
];

const Heart = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M12 21s-7-5.4-7-11a4.5 4.5 0 0 1 8-2.8A4.5 4.5 0 0 1 19 10c0 5.6-7 11-7 11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
);
const Network = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M17 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M10 11a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Zm11 9v-2a4 4 0 0 0-3-3.87M16 4.13A4 4 0 0 1 16 11.6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const Megaphone = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M3 11v2a1 1 0 0 0 1 1h2l5 4V6L6 10H4a1 1 0 0 0-1 1Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /><path d="M16 8a4 4 0 0 1 0 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
);
const Pulse = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M3 12h4l2-5 4 10 2-5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);
const Droplet = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M12 3s6 6.5 6 11a6 6 0 0 1-12 0c0-4.5 6-11 6-11Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>
);
const Database = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M3 5c0-1.1 4-2 9-2s9 .9 9 2-4 2-9 2-9-.9-9-2Zm0 0v14c0 1.1 4 2 9 2s9-.9 9-2V5M3 12c0 1.1 4 2 9 2s9-.9 9-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const ACHIEVEMENTS = [
  {
    icon: Network,
    title: "Every Registration Counts",
    points: [
      "8.8K+ total registrations across nine states, building a growing network of lifesavers.",
      "1,000+ QR code sticker locations established across hospitals, colleges, public spaces and transport hubs.",
      "Every registration helps strengthen community participation and improve access to blood donation support.",
    ],
  },
  {
    icon: Heart,
    title: "Powering a Lifesaving Ecosystem",
    points: [
      "6K+ blood units collected through 106+ organised blood donation camps.",
      "20+ hospital and NGO partnerships established to strengthen emergency blood access.",
      "Political and government stakeholders engaged in support of the Life Saver Army mission.",
    ],
  },
  {
    icon: Megaphone,
    title: "Donor Awareness and Registration Camps",
    points: [
      "106+ blood donation and awareness camps organised across Uttar Pradesh, Bihar, Maharashtra, Goa, Rajasthan, Delhi, Uttarakhand, Bengaluru and Hyderabad.",
      "University and college partnerships established with institutions including MGKVP University, BHU, and Sir Sunderlal Hospital, Varanasi.",
      "QR code sticker drive reaching 1,000+ locations, making donor registration accessible at the point of need.",
      "Every camp produced new registered donors who remain active and contactable when emergencies arise.",
    ],
  },
  {
    icon: Pulse,
    title: "Emergency Blood Support Milestones",
    points: [
      "First confirmed life saved through the Life Saver Army platform in June 2026, at Ram Krishna Mission Hospital, Varanasi; a patient connected to a donor through a QR code sticker placed at the hospital entrance.",
      "First cross-state emergency blood match facilitated, demonstrating the reach and responsiveness of the nationwide donor network.",
      "Rare blood type requests fulfilled for patients who had no local match through conventional channels.",
    ],
  },
  {
    icon: Droplet,
    title: "Platelet Donation Programme",
    points: [
      "Platelet donation awareness added to Life Saver Army's registration system, with donors now able to specify platelet availability for cancer and thalassaemia patients.",
      "Hospital partnerships expanded to include dedicated platelet recipient referral pathways, ensuring cancer patients have a reliable donor contact on file.",
    ],
  },
  {
    icon: Database,
    title: "Building a Nationwide Donor Registry",
    points: [
      "Life Saver Army's donor database spans across different states and covers all major blood groups, including rare types.",
      "Every registered donor is maintained as an active contact, reachable within minutes when a matching emergency is received.",
    ],
  },
];

const Check = (
  <svg viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>
);

const Achievements = ({ path }) => {
  const [metadata, setMetadata] = useState({ title: "JJFIndia | Achievements" });
  const [schemadata, setSchemadata] = useState({});

  const rootRef = useRef(null);
  useScrollReveal(rootRef);

  useEffect(() => {
    let tempMetadata = metadataJSON[path];
    let tempSchemadata = schemadataJSON[path];

    if (tempMetadata) {
      setMetadata(tempMetadata);
    } else {
      tempMetadata = {
        title: "JJFIndia | Achievements",
        description: "JJFIndia Impact and Stories page",
        keywords: "",
        robots: "index, follow",
        canonical: "https://jjfindia.com/impact/achievements",
        openGraph: {
          title: "JJFIndia | Achievements",
          description: "JJFIndia Impact and Stories page",
          url: "https://jjfindia.com/impact/achievements",
        },
        twitter: {
          card: "summary_large_image",
          title: "JJFIndia | Achievements",
          description: "JJFIndia Impact and Stories page",
        },
      };
      setMetadata(tempMetadata);
    }

    if (tempSchemadata) {
      setSchemadata(tempSchemadata);
    }
  }, [path]);

  const scrollTop = () => window.scrollTo(10, 0);

  return (
    <Fragment>
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          href="https://fonts.googleapis.com/css2?family=Anton&family=Inter:wght@400;500;600;700;800&display=swap"
          rel="stylesheet"
        />
        <title>{metadata.title}</title>
        <meta name="description" content={metadata.description} />
        <meta name="keywords" content={metadata.keywords} />
        <meta name="robots" content={metadata.robots} />
        <link rel="canonical" href={metadata.canonical} />
        <meta property="og:title" content={metadata.openGraph?.title} />
        <meta property="og:description" content={metadata.openGraph?.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta name="twitter:title" content={metadata.twitter?.title} />
        <meta name="twitter:description" content={metadata.twitter?.description} />
        <script type="application/ld+json">{JSON.stringify(schemadata, null, 2)}</script>
      </Helmet>

      <div className="jjfv2 about-live" ref={rootRef}>
        <HeaderV2 />

        <main className="page_content">
          {/* ---------- Page hero ---------- */}
          <AboutHero
            eyebrow="Milestones that matter"
            title="Achievements"
            crumbs={[
              { label: "Home", to: "/" },
              { label: "Impacts & Stories", to: "/impact/success-stories" },
              { label: "Achievements and Milestones" },
            ]}
          />

          {/* ---------- Intro ---------- */}
          <section className="section about" id="achievements-intro" style={{ paddingTop: "36px", paddingBottom: "36px" }}>
            <div className="wrap split" style={{ position: "relative", zIndex: 1 }}>
              <div className="about-media reveal">
                <span className="leaf-frame" aria-hidden="true"></span>
                <div className="ph">
                  <img src={AchievementsImg} alt="Donors with their certificates at a Life Saver Army blood donation camp" loading="lazy" />
                </div>
              </div>
              <div className="about-copy reveal d1">
                <span className="eyebrow">Milestones and recognition</span>
                <h2>What We've Built So Far?</h2>
                <p className="lead">
                  This page records the milestones Life Saver Army and JJF have reached: camps
                  organised, donors registered, partnerships formed. A running account of progress,
                  and of the work still ahead.
                </p>
              </div>
            </div>
          </section>

          {/* ---------- Key achievements ---------- */}
          <section className="section section--mist">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Transforming Lives, One Step at a Time</span>
                <h2>Key Achievements</h2>
              </div>

              <div className="ach-stats">
                {STATS.map((s, i) => (
                  <div className="ach-stat reveal" key={i}>
                    <div className="n">{s.n}{s.plus && <b>+</b>}</div>
                    <div className="l">{s.l}</div>
                  </div>
                ))}
              </div>

              <div className="achievements">
                {ACHIEVEMENTS.map((a, i) => (
                  <article className={"ach reveal" + (i % 2 ? " d1" : "")} key={i}>
                    <div className="ach__head">
                      <span className="ach__ico">{a.icon}</span>
                      <h3>{a.title}</h3>
                    </div>
                    {a.intro && <p className="ach__intro">{a.intro}</p>}
                    <ul className="ach__list">
                      {a.points.map((p, j) => (
                        <li key={j}>
                          {Check}
                          <span>{p}</span>
                        </li>
                      ))}
                    </ul>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* ---------- Looking ahead ---------- */}
          <section className="section">
            <div className="wrap">
              <div className="about-head">
                <span className="eyebrow">Together for a Future of Hope and Impact</span>
                <h2>Looking Ahead</h2>
                <p className="lead">
                  Life Saver Army, an initiative of Jainam Jivika Foundation, is just beginning. Every
                  new donor registration, every camp, and every partnership takes us closer to an
                  India where no blood emergency goes unanswered. We invite you to join us in making
                  that future real.
                </p>
                <Link to="/join-us/volunteer-opportunities" onClick={scrollTop} className="btn btn--green" style={{ marginTop: "1.7rem" }}>
                  Be Part of the Next Milestone
                  <svg viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </Link>
              </div>
            </div>
          </section>
        </main>

        <FooterV2 />
      </div>
    </Fragment>
  );
};

export default Achievements;
